import React, { useState } from 'react';

import { Tr, Text, Stack, Td, HStack, Button } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { Link, useParams } from 'react-router';

import CopyToClipboardButton from 'components/shared/CopyToClipboardButton';
import { DirectContribution } from 'hooks/data/datasets/directContribution/types';
import { LciaMethod } from 'hooks/data/datasets/lcia/types';
import useVersionInfo from 'hooks/useVersionInfo';
import { useDatasetSession } from 'pages/Dataset/DatasetSessionContext';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { formatNumericalValue } from 'utilities/numbers';

type Props = {
  contributor: DirectContribution;
  indicatorId: number;
  category: string;
  indicatorName: string;
  method: LciaMethod;
  index: number;
  hasProductColumn: boolean;
};

const DirectContributionRow = ({
  method,
  contributor,
  indicatorId,
  category,
  indicatorName,
  index,
  hasProductColumn,
}: Props) => {
  const { ver: version, system_model } = useVersionInfo();
  const { spold_id } = useParams();
  const [hovering, setHovering] = useState(false);
  const { increment } = useDatasetSession();

  return (
    <Tr onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
      <Td fontWeight={'semibold'}>
        {contributor?.relative_contribution ? formatNumericalValue(contributor?.relative_contribution * 100) + ' %' : null}
      </Td>
      <Td>
        <Stack>
          <Text>
            {formatNumericalValue(contributor.amount) + ' ' + contributor.unit}{' '}
            {contributor.type === 'elementary' ? contributor.meta.name : contributor.meta.subcomp}
          </Text>
        </Stack>
      </Td>
      {hasProductColumn && (
        <Td>
          {contributor.type === 'product' && (
            <Button
              wordBreak={'break-word'}
              whiteSpace={'normal'}
              as={Link}
              size="sm"
              variant="link"
              alignSelf={'flex-start'}
              px={0}
              to={`/${version}/${system_model}/dataset/${index}`}
              onClick={() => {
                mixpanel.track(MIXPANEL_EVENTS.DIRECT_CONTRIBUTION_SUPPLIER_LINK_CLICKED, {
                  spold_id,
                  supplier: contributor.meta.name,
                  exchange_index: contributor.index,
                });
                increment('impact_assessment_action_count');
              }}
            >
              {contributor.meta.name} - {contributor.meta.comp}
            </Button>
          )}
        </Td>
      )}
      <Td>
        <HStack justifyContent={hasProductColumn ? 'flex-start' : 'flex-end'}>
          <Text>{formatNumericalValue(contributor?.impact) ?? null}</Text>
          <CopyToClipboardButton
            value={contributor.impact}
            onValueCopied={() => {
              mixpanel.track(MIXPANEL_EVENTS.COPY_DATASET_VALUE, {
                spold_id,
                indicator_id: indicatorId,
                type: 'direct_contribution',
                supplier: contributor.meta.name,
                exchange_index: contributor.index,
                category,
                indicator_name: indicatorName,
                lcia_method: method.method_name,
              });
              increment('copy_dataset_value_count');
              increment('impact_assessment_action_count');
            }}
            opacity={hovering ? 1 : 0}
          />
        </HStack>
      </Td>
    </Tr>
  );
};

export default DirectContributionRow;
